import { useDialogs } from '../components/DialogProvider';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AudioLibraryPicker, { AudioSelection } from '../components/Wizard/AudioLibraryPicker';
import WizardChrome from '../components/Wizard/WizardChrome';
import AccountsStep from '../components/Wizard/steps/AccountsStep';
import ComposeStep from '../components/Wizard/steps/ComposeStep';
import PublishStep, { PublishPhase } from '../components/Wizard/steps/PublishStep';
import UploadStep from '../components/Wizard/steps/UploadStep';
import { dimensionsForAspect, migrateTimeline, withCoverFraming } from '../components/VideoEditor/migrateTimeline';
import apiClient, { API_URL } from '../services/api';
import { MaybePaginated, Post, SocialAccount } from '../types/api';
import { AspectRatio, ProjectAsset, VideoClip, VideoProject } from '../types/editor';
import {
  STEPS, WizardDraft, WizardStep, clearCurrent, clearDraft, emptyDraft,
  getCurrent, loadDraft, markCurrent, nextStep, prevStep, saveDraft, stepIndex, stepsForMode,
} from '../components/Wizard/wizardState';
import './WizardPage.css';

interface WizardPageProps {
  /** Title shown above the chrome. Defaults to "Start a post". */
  title?: string;
}

/**
 * Guided end-to-end flow: Upload → Edit → Accounts → Compose → Publish.
 *
 * Bootstraps a fresh VideoProject if the URL lacks a project id, then
 * routes the user through five focused screens. State persistence:
 *  - VideoProject is the backend store for assets / timeline / renders.
 *  - WizardDraft (description / accounts / scheduleFor) is mirrored to
 *    localStorage by project id so refresh + back-button preserve
 *    inputs.
 *  - Step 2 ("edit") hands off to the existing /editor/:id?wizard=1
 *    route — same full editor, just with the Use-in-post action
 *    relabeled to "Continue →" that comes back here.
 */
export default function WizardPage({ title }: WizardPageProps) {
  const { alert } = useDialogs();
  const navigate = useNavigate();
  const { projectId: projectIdParam, step: stepParam } =
    useParams<{ projectId?: string; step?: string }>();

  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const projectId = projectIdParam ? Number(projectIdParam) : null;
  const step: WizardStep = useMemo(() => {
    const s = (stepParam as WizardStep | undefined);
    return s && STEPS.includes(s) ? s : 'upload';
  }, [stepParam]);

  // Bootstrap: if no projectId, try to resume the most recent
  // in-progress wizard from localStorage; otherwise create a fresh
  // VideoProject and jump into step 1.
  // Resuming matters because / now redirects to /start — without this
  // guard every page reload of the root would spawn a new empty
  // project.
  useEffect(() => {
    if (projectId) {
      markCurrent(projectId);
      return;
    }
    let cancelled = false;
    const resumeId = getCurrent();
    const tryResume = async (): Promise<void> => {
      if (resumeId) {
        try {
          await apiClient.get<VideoProject>(`/editor/projects/${resumeId}/`);
          if (cancelled) return;
          navigate(`/start/${resumeId}/upload`, { replace: true });
          return;
        } catch {
          // Project got deleted (or 404) — drop the stale id and fall through.
          clearCurrent();
        }
      }
      try {
        const p = await apiClient.post<VideoProject>('/editor/projects/', {
          name: `Untitled — ${new Date().toLocaleString()}`,
          aspect: '9:16',
        });
        if (cancelled) return;
        markCurrent(p.id);
        navigate(`/start/${p.id}/upload`, { replace: true });
      } catch (e) {
        if (!cancelled) setBootstrapError((e as Error).message || 'Failed to start a new post');
      }
    };
    tryResume();
    return () => { cancelled = true; };
  }, [projectId, navigate]);

  // Draft state. Loaded from localStorage on project change; persisted
  // back on every edit so a refresh / back-button round trip never
  // loses what the user typed.
  const [draft, setDraft] = useState<WizardDraft>(emptyDraft);
  useEffect(() => {
    if (!projectId) return;
    setDraft(loadDraft(projectId));
  }, [projectId]);

  const updateDraft = useCallback((patch: Partial<WizardDraft>) => {
    setDraft((prev) => {
      const next = { ...prev, ...patch };
      if (projectId) saveDraft(projectId, next);
      return next;
    });
  }, [projectId]);

  /* User-controlled ordered upload list. Drives the Next button's
     enabled state AND feeds the timeline-build right before we hand
     off to the editor. */
  const [orderedAssets, setOrderedAssets] = useState<ProjectAsset[]>([]);

  /* Publish-step orchestration state. Held at the page level so the
   * wizard footer's primary button can trigger the action and reflect
   * its progress, while PublishStep stays a thin display component. */
  const [publishProject, setPublishProject] = useState<VideoProject | null>(null);
  const [publishAccounts, setPublishAccounts] = useState<SocialAccount[]>([]);
  const [publishPhase, setPublishPhase] = useState<PublishPhase>('idle');
  const [publishProgress, setPublishProgress] = useState(0);
  const [publishError, setPublishError] = useState<string | null>(null);

  /* Load the project + selected-account details once we land on the
   * publish step. Cheap re-fetch on every entry so the summary reflects
   * any edits the user made in earlier steps before coming back. */
  useEffect(() => {
    if (!projectId || step !== 'publish') return;
    setPublishError(null);
    Promise.all([
      apiClient.get<VideoProject>(`/editor/projects/${projectId}/`),
      apiClient.get<MaybePaginated<SocialAccount>>('/accounts/social-accounts/'),
    ]).then(([p, accs]) => {
      setPublishProject(p);
      const list = Array.isArray(accs) ? accs : accs.results || [];
      setPublishAccounts(list.filter((a) => draft.accountIds.includes(a.id)));
    }).catch((e: Error) => setPublishError(e.message || 'Load failed'));
  }, [projectId, step, draft.accountIds]);

  /* Wait for an aspect's render to finish via SSE. One-shot promise so
   * the orchestration in `runPublish` reads as straight-line code. */
  const waitForRender = useCallback(async (aspect: AspectRatio): Promise<void> => {
    if (!projectId) return;
    return new Promise<void>((resolve, reject) => {
      const url = `${API_URL}/editor/projects/${projectId}/progress/?aspect=${encodeURIComponent(aspect)}`;
      const es = new EventSource(url, { withCredentials: true });
      es.onmessage = (msg) => {
        try {
          const ev = JSON.parse(msg.data) as { phase: string; percent?: number; message?: string };
          if (typeof ev.percent === 'number') setPublishProgress(ev.percent);
          if (ev.phase === 'done') { es.close(); resolve(); }
          if (ev.phase === 'error') { es.close(); reject(new Error(ev.message || 'Render failed')); }
        } catch { /* heartbeat */ }
      };
      es.onerror = () => { /* server retries until close */ };
    });
  }, [projectId]);

  const runPublish = useCallback(async () => {
    if (!projectId || !publishProject) return;
    setPublishError(null);
    setPublishProgress(0);
    /* Instagram options — same rules the post form enforces. */
    const igSelected = publishAccounts
      .filter((a) => a.platform === 'instagram')
      .map((a) => a.id);
    const collaborators = draft.igCollaborators.filter((id) => igSelected.includes(id));
    const stories = draft.igStories.filter((id) => igSelected.includes(id));
    if (collaborators.length > 3) {
      setPublishPhase('error');
      setPublishError('Instagram allows at most 3 collaborators per post.');
      return;
    }
    if (collaborators.length > 0 && collaborators.length === igSelected.length) {
      setPublishPhase('error');
      setPublishError('At least one Instagram account must stay as the primary poster (not a collaborator).');
      return;
    }
    const platformOptions = (collaborators.length || stories.length)
      ? { instagram: { collaborators, stories } }
      : undefined;
    try {
      setPublishPhase('creating-post');
      const post = await apiClient.post<Post>('/posts/posts/', {
        description: draft.description,
        status: 'draft',
        social_accounts: draft.accountIds,
        platform_options: platformOptions,
      });

      if (draft.mode === 'photo') {
        setPublishPhase('attaching');
        const asset = (publishProject.assets || [])[0];
        if (asset?.file_url) {
          const resp = await fetch(asset.file_url, { credentials: 'include' });
          const blob = await resp.blob();
          const fd = new FormData();
          fd.append('file', blob, asset.original_filename || 'photo.jpg');
          fd.append('media_type', 'image');
          await apiClient.uploadFile(`/posts/posts/${post.id}/upload_media/`, fd);
        }
      } else {
        /* Render every aspect the selected platforms want (mirrors the
           backend's PLATFORM_ASPECT_PREFERENCE first picks) so the
           publisher can serve each platform its best fit — e.g. 1:1
           for Facebook/Bluesky feeds instead of a pillarboxed 9:16. */
        const PLATFORM_PREFERRED_ASPECT: Record<string, AspectRatio> = {
          bluesky: '1:1', instagram: '9:16', facebook: '1:1', tiktok: '9:16',
        };
        const targetAspect = (publishProject.aspect as AspectRatio) || '9:16';
        const needed = new Set<AspectRatio>([targetAspect]);
        for (const a of publishAccounts) {
          const pref = PLATFORM_PREFERRED_ASPECT[a.platform];
          if (pref) needed.add(pref);
        }
        const doneRenders = (publishProject.renders || [])
          .filter((r) => r.status === 'done' && r.file_url);
        const missing = Array.from(needed)
          .filter((asp) => !doneRenders.some((r) => r.aspect === asp));

        if (missing.length > 0) {
          setPublishPhase('rendering');
          await apiClient.post(`/editor/projects/${projectId}/render/`, {
            aspects: missing,
          });
          await Promise.all(missing.map((asp) => waitForRender(asp)));
        }

        setPublishPhase('attaching');
        await apiClient.post(`/editor/projects/${projectId}/attach_to_post/`, {
          post_id: post.id,
        });
      }

      let publishingParam = '';
      if (draft.scheduleFor) {
        const iso = new Date(draft.scheduleFor).toISOString();
        await apiClient.post(`/posts/posts/${post.id}/schedule/`, {
          scheduled_for: iso,
        });
      } else {
        await apiClient.post(`/posts/posts/${post.id}/publish/`, {});
        // Publish runs async in Celery — hand the post id to the
        // dashboard so it attaches to the progress stream instead of
        // showing a stale "draft" card.
        publishingParam = `?publishing=${post.id}`;
      }

      clearDraft(projectId);
      clearCurrent();
      navigate(`/dashboard${publishingParam}`);
    } catch (e) {
      setPublishPhase('error');
      setPublishError((e as Error).message || 'Publishing failed');
    }
  }, [projectId, publishProject, publishAccounts, draft, navigate, waitForRender]);

  const publishBusy = publishPhase !== 'idle' && publishPhase !== 'error';
  const canPublish = !!publishProject
    && publishAccounts.length > 0
    && !publishBusy;

  /* Build the project's timeline from the user's chosen upload order.
     Called once on the Upload→Edit transition so the editor opens
     pre-assembled in exactly the order the user dragged the tiles
     into. Audio assets land on the audio track (cap of one); video
     and image assets land on the video track in user order, with
     start times chained back-to-back. Mirrors the editor's own
     `ADD_CLIP` defaults so what the user sees in step 2 matches what
     they'd get if they dropped each asset onto the timeline manually. */
  const buildTimelineFromOrder = useCallback(async (
    assets: ProjectAsset[],
    audio: AudioSelection | null = null,
  ) => {
    if (!projectId) return;
    const project = await apiClient.get<VideoProject>(`/editor/projects/${projectId}/`);
    const timeline = migrateTimeline(project.timeline);
    const aspect = (project.aspect as AspectRatio) || '9:16';
    const dims = dimensionsForAspect(aspect);

    /* Rebuild the tracks in the user's chosen order, but REUSE existing
       clips per asset — clip ids key the per-aspect framing, trims, and
       transitions the user may have set in the editor, so a Back→Next
       round trip must keep them stable instead of minting fresh ids. */
    const prevVideoByAsset = new Map<number, VideoClip[]>();
    for (const c of timeline.video.clips) {
      const q = prevVideoByAsset.get(c.assetId) || [];
      q.push(c);
      prevVideoByAsset.set(c.assetId, q);
    }
    const prevAudioClips = timeline.audio.clips;
    timeline.video.clips = [];
    timeline.audio.clips = [];

    /* Library audio is copied into the project (bytes, not a reference)
       so editing or deleting the library entry later can't break this
       timeline. It wins over any audio file uploaded directly. The
       copy's id is remembered in the draft (audioCopy) so going Back
       and Next again reuses it instead of minting a duplicate; a copy
       from a previously-picked track is deleted when the track changes. */
    let libraryAudio: ProjectAsset | null = null;
    if (audio) {
      const prior = draft.audioCopy;
      const reusable = prior && prior.libraryId === audio.assetId
        ? (project.assets || []).find((a) => a.id === prior.projectAssetId) || null
        : null;
      if (reusable) {
        libraryAudio = reusable;
      } else {
        if (prior) {
          await apiClient
            .delete(`/editor/projects/${projectId}/assets/${prior.projectAssetId}/`)
            .catch(() => { /* already gone */ });
        }
        libraryAudio = await apiClient.post<ProjectAsset>(
          `/audiolib/audio/${audio.assetId}/use_in_project/`,
          { project_id: projectId },
        );
        updateDraft({
          audioCopy: { libraryId: audio.assetId, projectAssetId: libraryAudio.id },
        });
      }
    } else if (draft.audioCopy) {
      await apiClient
        .delete(`/editor/projects/${projectId}/assets/${draft.audioCopy.projectAssetId}/`)
        .catch(() => { /* already gone */ });
      updateDraft({ audioCopy: null });
    }

    const DEFAULT_IMAGE_DUR = 5;
    const uid = (prefix: string) =>
      `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

    /* Photo + music: hold the single photo long enough for the chosen
       audio segment to breathe (3–15s) instead of the 5s slide default. */
    const visuals = assets.filter((a) => a.kind !== 'audio');
    let imageDur = DEFAULT_IMAGE_DUR;
    if (libraryAudio && audio && visuals.length === 1 && visuals[0].kind === 'image') {
      const remaining = (libraryAudio.duration || 15) - audio.start;
      imageDur = Math.min(15, Math.max(3, remaining));
    }

    let cursor = 0;
    for (const a of assets) {
      if (a.kind === 'audio') {
        if (!libraryAudio && timeline.audio.clips.length === 0) {
          const reuseAudio = prevAudioClips.find((c) => c.assetId === a.id);
          timeline.audio.clips.push(reuseAudio ? { ...reuseAudio, start: 0 } : {
            id: uid('ac'),
            assetId: a.id,
            trimIn: 0,
            trimOut: a.duration || 30,
            start: 0,
            volume: 1.0,
          });
        }
        continue;
      }
      const reuse = prevVideoByAsset.get(a.id)?.shift();
      if (reuse) {
        timeline.video.clips.push({ ...reuse, start: cursor });
        cursor += reuse.trimOut - reuse.trimIn;
        continue;
      }
      const trimOut = a.kind === 'image'
        ? imageDur
        : (a.duration || DEFAULT_IMAGE_DUR);
      const clip = {
        id: uid('vc'),
        assetId: a.id,
        trimIn: 0,
        trimOut,
        start: cursor,
        transitionIn: { type: 'cut' as const, duration: 0.5 },
      };
      timeline.video.clips.push(clip);
      // New visual clips default to filling each aspect's canvas
      // ("cover" — overflow cropped) rather than letterboxing.
      timeline.aspects = withCoverFraming(timeline.aspects, clip.id, a.width, a.height);
      cursor += trimOut;
    }

    if (libraryAudio && audio && cursor > 0) {
      const srcDur = libraryAudio.duration || 0;
      const trimIn = Math.max(0, audio.start);
      // Clamp to the source length so the backend's over-trim check
      // passes; skip entirely if the marker leaves no audible audio.
      const trimOut = srcDur ? Math.min(srcDur, trimIn + cursor) : trimIn + cursor;
      if (trimOut > trimIn + 0.25) {
        const reuseAudio = prevAudioClips.find((c) => c.assetId === libraryAudio!.id);
        timeline.audio.clips = [{
          id: reuseAudio ? reuseAudio.id : uid('ac'),
          assetId: libraryAudio.id,
          trimIn,
          trimOut,
          start: 0,
          volume: reuseAudio ? reuseAudio.volume : 1.0,
        }];
      }
    }

    const end = Math.max(
      0,
      ...timeline.video.clips.map((c) => c.start + (c.trimOut - c.trimIn)),
      ...timeline.audio.clips.map((c) => c.start + (c.trimOut - c.trimIn)),
    );
    timeline.project.duration = Math.round(end * 100) / 100;
    timeline.project.aspect = aspect;
    timeline.project.width = dims.width;
    timeline.project.height = dims.height;

    await apiClient.patch(`/editor/projects/${projectId}/`, { timeline });
  }, [projectId, draft.audioCopy, updateDraft]);

  const goStep = useCallback((target: WizardStep) => {
    if (!projectId) return;
    if (target === 'edit') {
      // The "edit" step IS the existing editor surface, parametrized
      // with ?wizard=1 so it renders a "Continue →" CTA that bounces
      // back to the next wizard step.
      navigate(`/editor/${projectId}?wizard=1`);
      return;
    }
    navigate(`/start/${projectId}/${target}`);
  }, [navigate, projectId]);

  /* Held above the early-return guards below because hooks must run
     in a stable order every render (React's rules-of-hooks). */
  const [advancingFromUpload, setAdvancingFromUpload] = useState(false);
  const audioSelection: AudioSelection | null = useMemo(
    () => (draft.audioAssetId != null
      ? { assetId: draft.audioAssetId, start: draft.audioStart }
      : null),
    [draft.audioAssetId, draft.audioStart],
  );
  /* Picking / clearing music. Clearing also drops the project-asset
     copy a previous Next created, so it can't linger invisibly (the
     upload tiles hide it) or end up in a rebuilt timeline. */
  const handleAudioChange = useCallback((sel: AudioSelection | null) => {
    if (!sel && draft.audioCopy && projectId) {
      apiClient
        .delete(`/editor/projects/${projectId}/assets/${draft.audioCopy.projectAssetId}/`)
        .catch(() => { /* already gone */ });
      updateDraft({ audioAssetId: null, audioStart: 0, audioCopy: null });
      return;
    }
    updateDraft({
      audioAssetId: sel ? sel.assetId : null,
      audioStart: sel ? sel.start : 0,
    });
  }, [draft.audioCopy, projectId, updateDraft]);

  const advanceFromUpload = useCallback(async () => {
    setAdvancingFromUpload(true);
    try {
      await buildTimelineFromOrder(orderedAssets, audioSelection);
      goStep('edit');
    } catch (e) {
      void alert((e as Error).message || 'Failed to prepare timeline');
    } finally {
      setAdvancingFromUpload(false);
    }
  }, [buildTimelineFromOrder, orderedAssets, audioSelection, goStep, alert]);

  const advanceAsPhoto = useCallback(() => {
    updateDraft({ mode: 'photo' });
    goStep('accounts');
  }, [updateDraft, goStep]);

  const advanceAsVideo = useCallback(async () => {
    updateDraft({ mode: 'video' });
    await advanceFromUpload();
  }, [updateDraft, advanceFromUpload]);

  if (bootstrapError) {
    return (
      <div className="wizard-page">
        <div className="banner banner--error">{bootstrapError}</div>
      </div>
    );
  }
  if (!projectId) {
    return <div className="wizard-page muted">Starting a fresh post…</div>;
  }

  const mode = draft.mode;
  const steps = stepsForMode(mode);
  const idx = stepIndex(step, mode);
  const canAdvanceFromUpload = orderedAssets.length > 0;
  const canAdvanceFromAccounts = draft.accountIds.length > 0;
  const canAdvanceFromCompose = draft.description.trim().length > 0;
  const nextLabel = idx === steps.length - 1 ? 'Done' : 'Next →';

  const isSingleImage = orderedAssets.length === 1 && orderedAssets[0].kind === 'image';

  return (
    <div className="wizard-page">
      {step === 'upload' && (
        <WizardChrome
          step="upload"
          steps={steps}
          title={title || 'Add your media'}
          onNext={isSingleImage ? undefined : advanceFromUpload}
          nextLabel={advancingFromUpload ? 'Preparing…' : 'Next →'}
          nextDisabled={!canAdvanceFromUpload || advancingFromUpload}
          nextHint={canAdvanceFromUpload ? undefined : 'Upload at least one file to continue.'}
          hideNext={isSingleImage}
        >
          <UploadStep
            projectId={projectId}
            onAssetsChanged={setOrderedAssets}
            hiddenAssetIds={draft.audioCopy ? [draft.audioCopy.projectAssetId] : []}
          />
          <AudioLibraryPicker
            selection={audioSelection}
            onChange={handleAudioChange}
          />
          {isSingleImage && (
            <div className="wizard-mode-choice">
              {!audioSelection && (
                <button
                  type="button"
                  className="btn btn--primary wizard-mode-btn"
                  onClick={advanceAsPhoto}
                >
                  Post as Photo
                </button>
              )}
              <button
                type="button"
                className={audioSelection
                  ? 'btn btn--primary wizard-mode-btn'
                  : 'btn wizard-mode-btn'}
                onClick={advanceAsVideo}
                disabled={advancingFromUpload}
              >
                {advancingFromUpload
                  ? 'Preparing…'
                  : (audioSelection ? 'Post as Video with music' : 'Post as Video')}
              </button>
            </div>
          )}
        </WizardChrome>
      )}

      {step === 'accounts' && (
        <WizardChrome
          step="accounts"
          steps={steps}
          title="Where to Post?"
          onBack={() => { const p = prevStep('accounts', mode); if (p) goStep(p); }}
          onNext={() => { const n = nextStep('accounts', mode); if (n) goStep(n); }}
          nextLabel={nextLabel}
          nextDisabled={!canAdvanceFromAccounts}
          nextHint={canAdvanceFromAccounts ? undefined : 'Pick at least one account.'}
        >
          <AccountsStep
            selected={draft.accountIds}
            onChange={(ids) => updateDraft({ accountIds: ids })}
            igCollaborators={draft.igCollaborators}
            igStories={draft.igStories}
            onIgChange={(patch) => updateDraft(patch)}
          />
        </WizardChrome>
      )}

      {step === 'compose' && (
        <WizardChrome
          step="compose"
          steps={steps}
          title="Write your caption"
          onBack={() => { const p = prevStep('compose', mode); if (p) goStep(p); }}
          onNext={() => { const n = nextStep('compose', mode); if (n) goStep(n); }}
          nextLabel={nextLabel}
          nextDisabled={!canAdvanceFromCompose}
          nextHint={canAdvanceFromCompose ? undefined : 'Write a caption to continue.'}
        >
          <ComposeStep
            description={draft.description}
            onDescriptionChange={(v) => updateDraft({ description: v })}
            scheduleFor={draft.scheduleFor}
            onScheduleChange={(v) => updateDraft({ scheduleFor: v })}
          />
        </WizardChrome>
      )}

      {step === 'publish' && (
        <WizardChrome
          step="publish"
          steps={steps}
          title="Review and publish"
          onBack={() => { const p = prevStep('publish', mode); if (p) goStep(p); }}
          onNext={runPublish}
          nextLabel={publishBusy ? '…' : 'Post'}
          nextDisabled={!canPublish}
        >
          <PublishStep
            projectId={projectId}
            draft={draft}
            project={publishProject}
            accounts={publishAccounts}
            phase={publishPhase}
            progress={publishProgress}
            error={publishError}
          />
        </WizardChrome>
      )}
    </div>
  );
}
